import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { 
    CCard, CCardHeader, CCardBody, CButton, CSpinner, CRow, CCol, 
    CListGroup, CListGroupItem, CTable, CTableHead, CTableRow, 
    CTableHeaderCell, CTableBody, CTableDataCell, CFormInput, CFormCheck, CCollapse,
    CTooltip
} from '@coreui/react';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from "react-datepicker";
import ru from 'date-fns/locale/ru';
import enUS from 'date-fns/locale/en-US';
import dayjs from 'dayjs';
import { ResponsivePie } from '@nivo/pie';
import { useTranslation } from 'react-i18next';
import CIcon from '@coreui/icons-react';
import { cilSettings, cilCheckCircle, cilXCircle } from '@coreui/icons';

registerLocale('ru', ru);
registerLocale('en', enUS);

const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '00:00:00';
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const MultiCameraAnalytics = ({ selectedCameras }) => {
    const { t, i18n } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [startDate, setStartDate] = useState(dayjs().startOf('day').toDate());
    const [endDate, setEndDate] = useState(dayjs().endOf('day').toDate());
    const [minPercentage, setMinPercentage] = useState(5);
    const [showConfig, setShowConfig] = useState(false);

    // Матрица конфигурации: { [cameraId]: { uniqueVehicles: true, workTime: true, ... } }
    const [cameraConfig, setCameraConfig] = useState({});

    // Инициализация конфига при выборе камер
    useEffect(() => {
        setAnalytics(null);
        const initialConfig = {};
        selectedCameras.forEach(cam => {
            initialConfig[cam.id] = {
                uniqueVehicles: true,
                maxPeople: true,
                workTime: true,
                idleTime: true,
                downtime: true,
                efficiency: true
            };
        });
        setCameraConfig(initialConfig);
    }, [selectedCameras]);

    const handleConfigChange = (camId, metric) => {
        setCameraConfig(prev => ({
            ...prev,
            [camId]: {
                ...prev[camId],
                [metric]: !prev[camId][metric]
            }
        }));
    };

    const toggleAllForMetric = (metric, value) => {
        setCameraConfig(prev => {
            const newConfig = { ...prev };
            Object.keys(newConfig).forEach(camId => {
                newConfig[camId] = { ...newConfig[camId], [metric]: value };
            });
            return newConfig;
        });
    };

    const fetchReports = async () => {
        if (dayjs(endDate).isBefore(dayjs(startDate))) {
            toast.warn(t('common.error'));
            return;
        }
        setLoadingAnalytics(true);
        setAnalytics(null);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                camera_ids: selectedCameras.map(c => c.id),
                start_datetime: startDate.toISOString(),
                end_datetime: endDate.toISOString(),
                min_percentage: minPercentage
            };
            const response = await axios.post('/api/multi-camera-analytics/', payload, { headers: { Authorization: `Bearer ${token}` } });
            setAnalytics(response.data);
        } catch (error) {
            toast.error(error.response?.data?.error || t('common.error'));
        } finally {
            setLoadingAnalytics(false);
        }
    };

    // Динамический пересчет итогов на основе конфига
    const dynamicTotals = useMemo(() => {
        if (!analytics?.by_camera) return null;

        let totalUnique = 0;
        let totalMaxPeople = 0; // Теперь это будет МАКСИМУМ из всех постов
        let totalDowntime = 0;
        let totalIdle = 0;
        let totalWork = 0;

        Object.entries(analytics.by_camera).forEach(([camId, stats]) => {
            const config = cameraConfig[camId];
            if (!config) return;

            if (config.uniqueVehicles) totalUnique += (stats.unique_vehicles || 0);
            
            // Ищем максимальное значение среди включенных постов
            if (config.maxPeople) {
                const currentCamMax = stats.max_concurrent_people || 0;
                if (currentCamMax > totalMaxPeople) {
                    totalMaxPeople = currentCamMax;
                }
            }
            
            if (config.downtime) totalDowntime += (stats.total_downtime_seconds || 0);
            if (config.idleTime) totalIdle += (stats.total_idle_time_seconds || 0);
            if (config.workTime) totalWork += (stats.total_work_seconds || 0);
        });

        return {
            unique_vehicle_count: totalUnique,
            max_concurrent_people: totalMaxPeople,
            total_downtime_seconds: totalDowntime,
            total_idle_time_seconds: totalIdle,
            total_work_seconds: totalWork
        };
    }, [analytics, cameraConfig]);
    
    // Данные для круговой диаграммы на основе динамических итогов
    const { pieData, totalDuration } = useMemo(() => {
        if (!dynamicTotals) return { pieData: [], totalDuration: 0 };
        
        const data = [];
        if (dynamicTotals.total_downtime_seconds > 0) {
            data.push({ id: 'Downtime', label: t('classes.downtime_no_people'), value: dynamicTotals.total_downtime_seconds, color: '#6c757d' });
        }
        if (dynamicTotals.total_idle_time_seconds > 0) {
            data.push({ id: 'Idle', label: t('classes.idle_vehicle'), value: dynamicTotals.total_idle_time_seconds, color: '#ffc107' });
        }
        if (dynamicTotals.total_work_seconds > 0) {
            data.push({ id: 'Working', label: t('classes.working'), value: dynamicTotals.total_work_seconds, color: '#2eb85c' });
        }
        
        const total = data.reduce((sum, item) => sum + item.value, 0);
        return { pieData: data, totalDuration: total };
    }, [dynamicTotals, t]);

    // Данные для таблицы (просто подготовка, фильтрация происходит при рендере)
    const cameraTableData = useMemo(() => {
        if (!analytics?.by_camera) return [];
        return Object.entries(analytics.by_camera).map(([id, camStat]) => ({
            id: parseInt(id),
            name: camStat.name,
            uniqueVehicles: camStat.unique_vehicles || 0,
            workTime: camStat.total_work_seconds,
            idleTime: camStat.total_idle_time_seconds,
            downtime: camStat.total_downtime_seconds,
            maxPeople: camStat.max_concurrent_people || 0,
            efficiency: (camStat.total_work_seconds + camStat.total_idle_time_seconds) > 0 
                ? (camStat.total_work_seconds / (camStat.total_work_seconds + camStat.total_idle_time_seconds) * 100) 
                : 0
        }));
    }, [analytics]);

    const handleExportToExcel = async () => {
        if (!analytics) {
            toast.warn(t('common.select_period_msg'));
            return;
        }

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Monitoring System';
        workbook.created = new Date();

        const font = { name: 'Arial', size: 11 };
        const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        const headerStyle = { font: { ...font, bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } }, alignment: { vertical: 'middle', horizontal: 'center' }, border };
        const cellStyle = { font, border, alignment: { vertical: 'middle', horizontal: 'left' } };
        const excludedStyle = { font: { ...font, color: { argb: 'FF999999' }, italic: true }, border, alignment: { vertical: 'middle', horizontal: 'center' } };

        // --- ЛИСТ 1: ОБЩАЯ СВОДКА ---
        const wsSummary = workbook.addWorksheet(t('multi_cam.export_sheet_summary'));
        wsSummary.mergeCells('A1:C1');
        wsSummary.getCell('A1').value = t('multi_cam.export_title');
        wsSummary.getCell('A1').font = { size: 14, bold: true };
        
        wsSummary.getCell('A2').value = `${t('multi_cam.export_period')} ${dayjs(startDate).format('DD.MM.YYYY HH:mm')} - ${dayjs(endDate).format('DD.MM.YYYY HH:mm')}`;

        const summaryRows = [
            [t('multi_cam.export_param'), t('multi_cam.export_value')],
            [`${t('camera.unique_vehicles')} ${t('multi_cam.export_sum_suffix')}`, dynamicTotals.unique_vehicle_count],
            [`${t('camera.work_time')} ${t('multi_cam.export_sum_suffix')}`, formatDuration(dynamicTotals.total_work_seconds)],
            [`${t('camera.total_idle')} ${t('multi_cam.export_sum_suffix')}`, formatDuration(dynamicTotals.total_idle_time_seconds)],
            [`${t('camera.total_downtime')} ${t('multi_cam.export_sum_suffix')}`, formatDuration(dynamicTotals.total_downtime_seconds)],
            [`${t('camera.max_people')} ${t('multi_cam.export_max_suffix')}`, dynamicTotals.max_concurrent_people],
        ];

        let rowIdx = 5;
        summaryRows.forEach((row, i) => {
            const r = wsSummary.getRow(rowIdx + i);
            r.values = row;
            if (i === 0) r.eachCell(c => c.style = headerStyle);
            else r.eachCell(c => c.style = cellStyle);
        });
        wsSummary.columns = [{ width: 35 }, { width: 25 }];

        // --- ЛИСТ 2: ДЕТАЛИЗАЦИЯ ---
        const wsCameras = workbook.addWorksheet(t('multi_cam.export_sheet_details'));
        const camHeader = [
            t('multi_cam.col_post'), 
            t('multi_cam.col_unique'), 
            t('multi_cam.col_max_people'), 
            t('multi_cam.col_work_time'), 
            t('multi_cam.col_idle'), 
            t('multi_cam.col_downtime'), 
            t('multi_cam.col_efficiency')
        ];
        wsCameras.addRow(camHeader).eachCell(c => c.style = headerStyle);

        cameraTableData.forEach(cam => {
            const config = cameraConfig[cam.id] || {};
            const excludedText = t('multi_cam.export_excluded');
            const rowValues = [
                cam.name,
                config.uniqueVehicles ? cam.uniqueVehicles : excludedText,
                config.maxPeople ? cam.maxPeople : excludedText,
                config.workTime ? formatDuration(cam.workTime) : excludedText,
                config.idleTime ? formatDuration(cam.idleTime) : excludedText,
                config.downtime ? formatDuration(cam.downtime) : excludedText,
                config.efficiency ? cam.efficiency.toFixed(1) + '%' : excludedText
            ];
            
            const row = wsCameras.addRow(rowValues);
            
            // Стилизация ячеек в зависимости от того, включены они или нет
            row.getCell(1).style = cellStyle; // Имя камеры всегда активно
            
            const metricsOrder = ['uniqueVehicles', 'maxPeople', 'workTime', 'idleTime', 'downtime', 'efficiency'];
            metricsOrder.forEach((metric, idx) => {
                const cellIndex = idx + 2; // +2 т.к. 1-based index и 1-я колонка имя
                if (config[metric]) {
                    row.getCell(cellIndex).style = cellStyle;
                } else {
                    row.getCell(cellIndex).style = excludedStyle;
                }
            });
        });

        wsCameras.columns = [{ width: 25 }, { width: 18 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 10 }];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `${t('multi_cam.export_filename')}_${dayjs(startDate).format('YYYY-MM-DD')}.xlsx`;
        saveAs(blob, fileName);
    };

    return (
        <CCol md={12} className="mt-4">
            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <span>{t('multi_cam.summary_title', { count: selectedCameras.length })}</span>
                    {analytics && !loadingAnalytics && (
                        <div className="d-flex gap-2">
                            <CButton color="secondary" variant={showConfig ? "outline" : "solid"} size="sm" onClick={() => setShowConfig(!showConfig)}>
                                <CIcon icon={cilSettings} className="me-2"/>
                                {showConfig ? t('multi_cam.btn_hide_config') : t('multi_cam.btn_show_config')}
                            </CButton>
                            <CButton color="success" onClick={handleExportToExcel} className="text-white" size="sm">
                                {t('common.export_excel')}
                            </CButton>
                        </div>
                    )}
                </CCardHeader>
                <CCardBody>
                    <CRow className="align-items-end mb-4">
                        <CCol md={3} className="mb-2 mb-md-0"><label>{t('common.date_start')}</label><DatePicker selected={startDate} onChange={(date) => setStartDate(date)} selectsStart startDate={startDate} endDate={endDate} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></CCol>
                        <CCol md={3} className="mb-2 mb-md-0"><label>{t('common.date_end')}</label><DatePicker selected={endDate} onChange={(date) => setEndDate(date)} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></CCol>
                        <CCol md={4} className="mb-2 mb-md-0">
                            <label>{t('multi_cam.min_percentage_label')}</label>
                            <CFormInput type="number" min="0" max="100" value={minPercentage} onChange={(e) => setMinPercentage(e.target.value)} title={t('multi_cam.min_percentage_title')} />
                        </CCol>
                        <CCol md={2}><CButton color="primary" onClick={fetchReports} disabled={loadingAnalytics} className="w-100">{loadingAnalytics ? <CSpinner size="sm"/> : t('common.get_report')}</CButton></CCol>
                    </CRow>

                    {loadingAnalytics && <div className="text-center p-3"><CSpinner/></div>}
                    
                    {analytics && !loadingAnalytics && dynamicTotals && (
                        <>
                            {/* Блок конфигурации параметров */}
                            <CCollapse visible={showConfig}>
                                <div className="p-3 mb-4 border rounded bg-light">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="m-0">{t('multi_cam.config_title')}</h6>
                                        <div className="small text-muted">{t('multi_cam.config_subtitle')}</div>
                                    </div>
                                    <div className="table-responsive" style={{maxHeight: '300px'}}>
                                        <CTable small bordered hover className="bg-white mb-0">
                                            <CTableHead color="light" style={{position: 'sticky', top: 0}}>
                                                <CTableRow>
                                                    <CTableHeaderCell>{t('multi_cam.col_post')}</CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span>{t('multi_cam.col_unique')}</span>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('uniqueVehicles', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('uniqueVehicles', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span>{t('multi_cam.col_max_people')}</span>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('maxPeople', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('maxPeople', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span>{t('multi_cam.col_work_time')}</span>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('workTime', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('workTime', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span>{t('multi_cam.col_idle')}</span>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('idleTime', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('idleTime', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span>{t('multi_cam.col_downtime')}</span>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('downtime', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('downtime', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '80px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span>{t('multi_cam.col_efficiency')}</span>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('efficiency', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('efficiency', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                </CTableRow>
                                            </CTableHead>
                                            <CTableBody>
                                                {cameraTableData.map(cam => (
                                                    <CTableRow key={cam.id}>
                                                        <CTableDataCell><strong>{cam.name}</strong></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.uniqueVehicles} onChange={() => handleConfigChange(cam.id, 'uniqueVehicles')}/></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.maxPeople} onChange={() => handleConfigChange(cam.id, 'maxPeople')}/></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.workTime} onChange={() => handleConfigChange(cam.id, 'workTime')}/></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.idleTime} onChange={() => handleConfigChange(cam.id, 'idleTime')}/></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.downtime} onChange={() => handleConfigChange(cam.id, 'downtime')}/></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.efficiency} onChange={() => handleConfigChange(cam.id, 'efficiency')}/></CTableDataCell>
                                                    </CTableRow>
                                                ))}
                                            </CTableBody>
                                        </CTable>
                                    </div>
                                </div>
                            </CCollapse>

                            <CRow>
                                <CCol md={6}>
                                    <h5 className="mb-3">{t('multi_cam.general_summary')} <small className="text-muted">{t('multi_cam.summary_dynamic')}</small></h5>
                                    <CListGroup flush className="mb-3">
                                        <CListGroupItem><strong>{t('camera.unique_vehicles')}:</strong> {dynamicTotals.unique_vehicle_count}</CListGroupItem>
                                        <CListGroupItem>
                                            <strong>{t('camera.max_people')}:</strong> {dynamicTotals.max_concurrent_people}
                                            <span className="text-muted ms-2 small">({'>'} {minPercentage}% от времени работы)</span>
                                        </CListGroupItem>
                                        <CListGroupItem><strong>{t('camera.total_downtime')}:</strong> {formatDuration(dynamicTotals.total_downtime_seconds)}</CListGroupItem>
                                        <CListGroupItem><strong>{t('camera.total_idle')}:</strong> {formatDuration(dynamicTotals.total_idle_time_seconds)}</CListGroupItem>
                                        <CListGroupItem><strong>{t('camera.work_time')}:</strong> {formatDuration(dynamicTotals.total_work_seconds)}</CListGroupItem>
                                    </CListGroup>
                                </CCol>
                                <CCol md={6}>
                                    <div style={{ height: '300px' }}>
                                        {pieData.length > 0 ? (
                                            <ResponsivePie
                                                data={pieData}
                                                margin={{ top: 20, right: 140, bottom: 20, left: 20 }}
                                                innerRadius={0.6}
                                                padAngle={1}
                                                cornerRadius={3}
                                                activeOuterRadiusOffset={8}
                                                colors={{ datum: 'data.color' }}
                                                enableArcLinkLabels={false}
                                                arcLabelsSkipAngle={15}
                                                arcLabelsTextColor="#ffffff"
                                                arcLabel={d => totalDuration > 0 ? `${((d.value / totalDuration) * 100).toFixed(0)}%` : ''}
                                                tooltip={({ datum }) => (
                                                    <div style={{ background: 'white', padding: '5px 10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                                                        <strong>{datum.label}</strong>: {formatDuration(datum.value)}
                                                    </div>
                                                )}
                                                legends={[{
                                                    anchor: 'right', direction: 'column', justify: false, translateX: 120, translateY: 0,
                                                    itemsSpacing: 10, itemWidth: 100, itemHeight: 18, itemTextColor: '#999',
                                                    symbolSize: 14, symbolShape: 'circle', effects: [{ on: 'hover', style: { itemTextColor: '#000' } }]
                                                }]}
                                            />
                                        ) : <p className="text-center text-muted pt-5">Нет данных для графика (проверьте настройки)</p>}
                                    </div>
                                </CCol>
                            </CRow>
                            
                            <h5 className="mt-4 mb-3">{t('multi_cam.stats_by_posts')}</h5>
                            <div className="table-responsive">
                                <CTable hover bordered>
                                    <CTableHead color="light">
                                        <CTableRow>
                                            <CTableHeaderCell>{t('multi_cam.col_post')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('multi_cam.col_unique')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('multi_cam.col_max_people')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('multi_cam.col_work_time')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('multi_cam.col_idle')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('multi_cam.col_downtime')}</CTableHeaderCell>
                                            <CTableHeaderCell>{t('multi_cam.col_efficiency')}</CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {cameraTableData.map((cam, idx) => {
                                            const config = cameraConfig[cam.id] || {};
                                            const mutedStyle = { color: '#adb5bd', textDecoration: 'line-through' };
                                            return (
                                                <CTableRow key={idx}>
                                                    <CTableDataCell><strong>{cam.name}</strong></CTableDataCell>
                                                    <CTableDataCell style={!config.uniqueVehicles ? mutedStyle : {}}>{cam.uniqueVehicles}</CTableDataCell>
                                                    <CTableDataCell style={!config.maxPeople ? mutedStyle : {}}>{cam.maxPeople}</CTableDataCell>
                                                    <CTableDataCell style={!config.workTime ? mutedStyle : {}} className={config.workTime ? "text-success" : ""}>{formatDuration(cam.workTime)}</CTableDataCell>
                                                    <CTableDataCell style={!config.idleTime ? mutedStyle : {}} className={config.idleTime ? "text-warning" : ""}>{formatDuration(cam.idleTime)}</CTableDataCell>
                                                    <CTableDataCell style={!config.downtime ? mutedStyle : {}} className={config.downtime ? "text-secondary" : ""}>{formatDuration(cam.downtime)}</CTableDataCell>
                                                    <CTableDataCell style={!config.efficiency ? mutedStyle : {}}><strong>{cam.efficiency.toFixed(1)}%</strong></CTableDataCell>
                                                </CTableRow>
                                            );
                                        })}
                                    </CTableBody>
                                </CTable>
                                <p className="text-muted small">{t('multi_cam.excluded_note')}</p>
                            </div>
                        </>
                    )}
                </CCardBody>
            </CCard>
        </CCol>
    );
};

export default MultiCameraAnalytics;